import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import Dinero from 'dinero.js'
import { Link } from 'react-router-dom'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { makeStyles } from '@mui/styles'
import { IconButton, InputAdornment } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { useUpdatePriceEntryMutation } from 'services/priceEntries'
import {
  PRICE_TYPES,
  DEFAULT_PRECISION,
  CHURN_ZERO_EVENTS as cz,
  MEASURE_UNIT,
  MEASURE_UNIT_ARRAY,
} from 'utils/constants'
import { setDecimalPrecision, unformatStringNumber } from 'utils/helpers'
import AlertSnackbar from 'components/generic/snackbars'
import DataGridWithSearch from 'components/generic/datagrid/DataGridWithSearch'
import PricingFormulaModal from 'components/generic/modals/dialog-modal/PricingFormulaModal'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import Loading from 'components/generic/Loading'
import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import { trackEvent } from 'utils/tracking'
import { checkIfPriceEntryIsEdited } from 'components/containers/commodities/price-lists/utils'
import PriceEntryButtons from 'components/containers/commodities/price-entries/PriceEntryButtons'

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiDataGrid-columnSeparator': { visibility: 'hidden' },
    '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
    '& .MuiDataGrid-cell:focus': { outline: 'none' },
    '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
  },
}))

const PriceEntriesList = ({
  priceEntriesList,
  loading,
  filter,
  setFilter,
  setDirtyFields,
  rowCount,
  paginationOptions,
  setPaginationOptions,
}) => {
  const classes = useStyles()

  const { control, setValue, getValues } = useFormContext()

  const apiRef = useGridApiRef()
  const [errorMessage, setErrorMessage] = useState('')
  const [openPricingFormulaModal, setOpenPricingFormulaModal] = useState(false)
  const [viewModeForFormula, setViewModeForFormula] = useState(false)
  const [selectedPriceEntry, setSelectedPriceEntry] = useState()
  const [updatePriceEntry, { isLoading: isLoadingUpdatePriceEntry }] = useUpdatePriceEntryMutation()
  const { isAdmin, viewPermissions } = useSelector(state => state.permissions)
  const priceWritePermission = isAdmin || viewPermissions?.commodities?.priceWrite
  const { decimalPrecision: decimalPrecisionSetting = 2 } = useSelector(
    state => state.settings?.settings?.purchaseSettings?.inboundTickets || {},
  )
  const { defaultCurrency, settings, defaultYardUm } = useSelector(state => state.settings)
  const { disableUmDropdowns } = settings?.purchaseSettings?.inboundTickets || {}

  const handleChange = (event, row) => {
    const cellName = event?.target?.name?.split('.').pop()
    const newValue = event?.target?.value || 0
    apiRef.current.updateRows([{ id: row.id, [`${cellName}Value`]: newValue }])

    setDirtyFields(prevState => {
      const newState = { ...prevState }
      if (checkIfPriceEntryIsEdited(row, { ...getValues(row.id), [cellName]: newValue })) {
        newState[row.id] = true
      } else {
        delete newState[row.id]
      }
      return newState
    })
  }

  const onUndoPriceEntry = row => {
    setValue(`${row.id}.buyPrice`, setDecimalPrecision(Dinero({ ...row?.buyPrice?.price }).toUnit()))
    setValue(`${row.id}.minMaxBuyRange`, row?.buyPrice?.minMaxRange)
    setValue(`${row.id}.sellPrice`, setDecimalPrecision(Dinero({ ...row?.sellPrice?.price }).toUnit()))
    setValue(`${row.id}.minMaxSellRange`, row?.sellPrice?.minMaxRange)
    setValue(`${row.id}.weightUnits`, row?.weightUnits)
  }

  const createSavePriceEntryPayload = row => {
    const { id, buyPrice, sellPrice, materialTypeId, priceSheetId } = row
    const updatedPriceEntry = { id, materialTypeId, priceSheetId, buyPrice, sellPrice }
    updatedPriceEntry.weightUnits = getValues(`${id}.weightUnits`)
    if (buyPrice?.priceType !== PRICE_TYPES.FORMULA.id) {
      updatedPriceEntry.buyPrice = {
        ...buyPrice,
        isBuyPrice: true,
        priceType: PRICE_TYPES.SPOT.id,
        price: {
          amount: unformatStringNumber(getValues(`${id}.buyPrice`)) * 10 ** DEFAULT_PRECISION,
          currency: defaultCurrency,
          precision: DEFAULT_PRECISION,
        },
        minMaxRange: unformatStringNumber(getValues(`${id}.minMaxBuyRange`) || 0),
      }
    }
    if (sellPrice?.priceType !== PRICE_TYPES.FORMULA.id) {
      updatedPriceEntry.sellPrice = {
        ...sellPrice,
        isBuyPrice: false,
        priceType: PRICE_TYPES.SPOT.id,
        price: {
          amount: unformatStringNumber(getValues(`${id}.sellPrice`)) * 10 ** DEFAULT_PRECISION,
          currency: defaultCurrency,
          precision: DEFAULT_PRECISION,
        },
        minMaxRange: unformatStringNumber(getValues(`${id}.minMaxSellRange`) || 0),
      }
    }
    return {
      priceEntryId: id,
      materialTypeId: updatedPriceEntry.materialTypeId,
      priceEntry: updatedPriceEntry,
    }
  }

  const handleFormulaClick = (event, row, isFormula) => {
    event.stopPropagation()
    setOpenPricingFormulaModal(true)
    setViewModeForFormula(isFormula)
    setSelectedPriceEntry(row)
  }

  const onSavePriceFormula = useCallback(
    async (priceEntryId = '', updatedPriceEntry = {}) => {
      if (viewModeForFormula) {
        setViewModeForFormula(false)
      } else {
        const buyPriceType = updatedPriceEntry?.buyPrice?.priceType
        const buyBenchmarkId = updatedPriceEntry?.buyPrice?.benchmarkId
        const buyReferencePriceEntryId = updatedPriceEntry?.buyPrice?.referencePriceEntryId
        const sellPriceType = updatedPriceEntry?.sellPrice?.priceType
        const sellBenchmarkId = updatedPriceEntry?.sellPrice?.benchmarkId
        const sellReferencePriceEntryId = updatedPriceEntry?.sellPrice?.referencePriceEntryId

        if (
          (buyPriceType === PRICE_TYPES.FORMULA.id && !buyBenchmarkId && !buyReferencePriceEntryId) ||
          (sellPriceType === PRICE_TYPES.FORMULA.id && !sellBenchmarkId && !sellReferencePriceEntryId)
        ) {
          setErrorMessage('Reference Price is required.')
        } else {
          setOpenPricingFormulaModal(false)
          await updatePriceEntry({
            priceEntryId,
            materialTypeId: updatedPriceEntry.materialTypeId,
            priceEntry: updatedPriceEntry,
          })
            .unwrap()
            .then(() => {
              const { id, weightUnits, buyPrice, sellPrice } = updatedPriceEntry
              apiRef.current.updateRows([
                {
                  id,
                  weightUnits: weightUnits,
                  buyPrice,
                  sellPrice,
                  minMaxBuyRange: buyPrice?.minMaxRange,
                  minMaxSellRange: sellPrice?.minMaxRange,
                },
              ])
              setValue(`${id}.buyPrice`, setDecimalPrecision(Dinero({ ...buyPrice?.price }).toUnit()))
              setValue(`${id}.minMaxBuyRange`, buyPrice?.minMaxRange)
              setValue(`${id}.sellPrice`, setDecimalPrecision(Dinero({ ...sellPrice?.price }).toUnit()))
              setValue(`${id}.minMaxSellRange`, sellPrice?.minMaxRange)
              setValue(`${id}.weightUnits`, weightUnits)
              setDirtyFields(prevState => {
                const newState = { ...prevState }
                delete newState[id]
                return newState
              })
              trackEvent(cz.UPDATE_PRICING_FORMULA)
            })
            .catch(error => {
              const errorMsg = error?.data?.error || 'An error occurred updating the priceEntry.'
              setErrorMessage(errorMsg)
              trackEvent({ eventName: cz.UPDATE_PRICING_FORMULA, errorMsg })
            })
        }
      }
    },
    [viewModeForFormula],
  )

  const priceEntriesColumns = useMemo(() => {
    const columns = [
      {
        field: 'name',
        headerName: 'Material Name',
        flex: 0.8,
        renderCell: ({ row: { name, commodityId, materialTypeId } }) => {
          const materialName = encodeURIComponent(name)
          return <Link to={`/app/commodities/${commodityId}/${materialName}/${materialTypeId}/overview`}>{name}</Link>
        },
      },
      {
        field: 'code',
        headerName: 'Code',
        flex: 0.7,
      },
      {
        field: 'buyPrice',
        headerName: 'Default Buy',
        flex: 0.7,
        headerAlign: 'right',
        align: 'right',
        renderCell: ({ row }) => {
          const { id, buyPrice } = row
          const { priceType } = buyPrice
          const isFormula = priceType === 'FORMULA'
          const value = setDecimalPrecision(Dinero({ ...buyPrice?.price }).toUnit())
          const units = getValues(`${id}.weightUnits`)

          return (
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
              }}
            >
              {isFormula || !priceWritePermission ? (
                <span>{`$${value}/${disableUmDropdowns ? defaultYardUm : units}`}</span>
              ) : (
                <Input
                  control={control}
                  name={`${id}.buyPrice`}
                  sx={{ width: '100%' }}
                  defaultValue={value}
                  InputProps={{
                    inputComponent: InputFormat,
                    sx: { height: '40px', fontSize: '13px' },
                    inputProps: {
                      inputType: 'price',
                      decimalScale: decimalPrecisionSetting,
                      fixedDecimalScale: true,
                      suffix: `/${disableUmDropdowns ? defaultYardUm : units || defaultYardUm}`,
                      onChange: event => handleChange(event, row),
                      onFocus: event => setTimeout(() => event.target.focus(), 100),
                      style: {
                        fontSize: '0.875rem',
                        textAlign: 'right',
                      },
                    },
                  }}
                />
              )}
            </div>
          )
        },
      },
      {
        field: 'minMaxBuyRange',
        headerName: 'Min/Max Buy Range',
        flex: 0.5,
        headerAlign: 'right',
        align: 'right',
        sortable: false,
        renderCell: ({ row }) => {
          const { id, buyPrice } = row
          const { minMaxRange, priceType } = buyPrice
          const isFormula = priceType === 'FORMULA'
          return (
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
              }}
            >
              {isFormula || !priceWritePermission ? (
                <span>{`$${setDecimalPrecision(minMaxRange)}`}</span>
              ) : (
                <Input
                  control={control}
                  name={`${id}.minMaxBuyRange`}
                  defaultValue={minMaxRange}
                  type="number"
                  pattern="^\d+(\.\d{0,2})?$"
                  fractionDigits={decimalPrecisionSetting}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    sx: { height: '40px', fontSize: '13px', minWidth: '100px' },
                    inputProps: {
                      onChange: event => {
                        setValue(`${id}.minMaxBuyRange`, event.target.value)
                        handleChange(event, row)
                      },
                      onFocus: event => setTimeout(() => event.target.focus(), 100),
                      step: 0.01,
                      min: 0.0,
                      style: {
                        fontSize: '0.875rem',
                        textAlign: 'right',
                      },
                    },
                  }}
                />
              )}
            </div>
          )
        },
      },
      {
        field: 'sellPrice',
        headerName: 'Default Sell',
        flex: 0.7,
        headerAlign: 'right',
        align: 'right',
        renderCell: ({ row }) => {
          const { id, sellPrice } = row
          const { priceType } = sellPrice
          const isFormula = priceType === 'FORMULA'
          const value = setDecimalPrecision(Dinero({ ...sellPrice?.price }).toUnit())
          const units = getValues(`${id}.weightUnits`)

          return (
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
              }}
            >
              {isFormula || !priceWritePermission ? (
                <span>{`$${value}/${disableUmDropdowns ? defaultYardUm : units}`}</span>
              ) : (
                <Input
                  control={control}
                  name={`${id}.sellPrice`}
                  sx={{ width: '100%' }}
                  defaultValue={value}
                  InputProps={{
                    inputComponent: InputFormat,
                    sx: { height: '40px', fontSize: '13px' },
                    inputProps: {
                      inputType: 'price',
                      decimalScale: decimalPrecisionSetting,
                      fixedDecimalScale: true,
                      suffix: `/${disableUmDropdowns ? defaultYardUm : units || defaultYardUm}`,
                      onChange: event => handleChange(event, row),
                      onFocus: event => setTimeout(() => event.target.focus(), 100),
                      style: {
                        fontSize: '0.875rem',
                        textAlign: 'right',
                      },
                    },
                  }}
                />
              )}
            </div>
          )
        },
      },
      {
        field: 'minMaxSellRange',
        headerName: 'Min/Max Sell Range',
        flex: 0.5,
        headerAlign: 'right',
        align: 'right',
        sortable: false,
        renderCell: ({ row }) => {
          const { id, sellPrice } = row
          const { minMaxRange, priceType } = sellPrice
          const isFormula = priceType === 'FORMULA'
          return (
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
              }}
            >
              {isFormula || !priceWritePermission ? (
                <span>{`$${setDecimalPrecision(minMaxRange)}`}</span>
              ) : (
                <Input
                  control={control}
                  name={`${id}.minMaxSellRange`}
                  defaultValue={minMaxRange}
                  type="number"
                  pattern="^\d+(\.\d{0,2})?$"
                  fractionDigits={decimalPrecisionSetting}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    sx: { height: '40px', fontSize: '13px', minWidth: '100px' },
                    inputProps: {
                      onChange: event => {
                        setValue(`${id}.minMaxSellRange`, event.target.value)
                        handleChange(event, row)
                      },
                      onFocus: event => setTimeout(() => event.target.focus(), 100),
                      step: 0.01,
                      min: 0.0,
                      style: {
                        fontSize: '0.875rem',
                        textAlign: 'right',
                      },
                    },
                  }}
                />
              )}
            </div>
          )
        },
      },
      {
        field: 'weightUnits',
        headerName: 'UM',
        flex: 0.5,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
        renderCell: ({ id, row }) => {
          const { weightUnits } = row
          return (
            <Select
              control={control}
              name={`${id}.weightUnits`}
              data-test={`weightUnits-${id}`}
              defaultValue={weightUnits}
              items={MEASURE_UNIT_ARRAY}
              selectProps={{
                sx: { height: '40px', fontSize: '13px' },
                onChange: event => {
                  setValue(`${id}.weightUnits`, event.target.value)
                  handleChange(event, row)
                },
              }}
            />
          )
        },
      },
      {
        field: 'priceType',
        headerName: 'Formula',
        renderCell: ({ row }) => {
          const { buyPrice, sellPrice } = row
          const isFormula =
            buyPrice?.priceType === PRICE_TYPES.FORMULA.id || sellPrice?.priceType === PRICE_TYPES.FORMULA.id

          return (
            <IconButton
              color={isFormula ? 'primary' : 'default'}
              disabled={!priceWritePermission}
              size="small"
              onClick={event => handleFormulaClick(event, row, isFormula)}
            >
              {isFormula ? <EditIcon fontSize="small" /> : <AddCircleIcon fontSize="small" />}
            </IconButton>
          )
        },
        headerAlign: 'center',
        align: 'center',
        sortable: false,
      },
      {
        field: 'actions',
        headerName: '',
        renderCell: ({ row }) => {
          const enableButtons = checkIfPriceEntryIsEdited(row, getValues(row.id))

          return (
            <PriceEntryButtons
              disabled={!enableButtons}
              useMutation={useUpdatePriceEntryMutation}
              payload={createSavePriceEntryPayload(row)}
              onSuccess={response => {
                apiRef.current.updateRows([
                  {
                    id: row.id,
                    buyPrice: response.buyPrice,
                    sellPrice: response.sellPrice,
                    weightUnits: response.weightUnits,
                    minMaxBuyRange: response.buyPrice.minMaxRange,
                    minMaxSellRange: response.sellPrice.minMaxRange,
                  },
                ])
                setDirtyFields(prevState => {
                  const newState = { ...prevState }
                  delete newState[row.id]
                  return newState
                })
                trackEvent(cz.UPDATE_MATERIAL_PRICES)
              }}
              onError={error => {
                const errorMsg = error?.data?.error || 'An error occurred updating the price entry.'
                trackEvent({ eventName: cz.UPDATE_MATERIAL_PRICES, errorMsg })
                setErrorMessage(errorMsg)
              }}
              onUndo={() => onUndoPriceEntry(row)}
            />
          )
        },
        headerAlign: 'center',
        align: 'center',
        sortable: false,
      },
    ]
    return columns.filter(column => {
      if (column.field === 'weightUnits') {
        return !disableUmDropdowns
      }
      return true
    })
  }, [disableUmDropdowns, defaultYardUm])

  return (
    <>
      {isLoadingUpdatePriceEntry && <Loading />}
      <AlertSnackbar
        message={errorMessage}
        isOpen={!!errorMessage}
        severity="error"
        handleClose={() => setErrorMessage('')}
      />
      <DataGridWithSearch
        className={classes.root}
        columns={priceEntriesColumns}
        rows={priceEntriesList}
        loading={loading}
        apiRef={apiRef}
        useBackendSortAndFilter
        filter={filter}
        setFilter={setFilter}
        autoPageSize={false}
        paginationMode="server"
        rowCount={rowCount}
        onPageChange={newPageNumber => setPaginationOptions(prevState => ({ ...prevState, page: newPageNumber + 1 }))}
        onPageSizeChange={newPageSize => setPaginationOptions(prevState => ({ ...prevState, pageSize: newPageSize }))}
        pageSize={paginationOptions.pageSize}
        rowsPerPageOptions={[10, 20, 30, 50]}
      />
      <PricingFormulaModal
        title={`Pricing Formula - ${selectedPriceEntry?.name}`}
        open={openPricingFormulaModal}
        priceEntry={selectedPriceEntry}
        isViewMode={viewModeForFormula}
        onSave={onSavePriceFormula}
        onCloseModal={() => setOpenPricingFormulaModal(false)}
      />
    </>
  )
}

PriceEntriesList.propTypes = {
  priceEntriesList: PropTypes.array,
  loading: PropTypes.bool,
  filter: PropTypes.object,
  setFilter: PropTypes.func,
  setDirtyFields: PropTypes.func,
  rowCount: PropTypes.number,
  paginationOptions: PropTypes.object.isRequired,
  setPaginationOptions: PropTypes.func.isRequired,
}

PriceEntriesList.defaultProps = {
  priceEntriesList: [],
  loading: false,
  filter: {},
  setFilter: () => {},
  setDirtyFields: () => {},
  rowCount: 0,
}

export default PriceEntriesList
